import type {
  AreaChatState,
  AreaSummaryDto,
  OnboardingState,
  ProcessCandidateDto,
} from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle } from '../../ui';
import { ActivityLoader, drafting, MessageBubble } from './Onboarding';
import { AreaPhases, SetupBanner, useSetupProgress } from './Setup';
import { ScopeControl } from './SiteScope';

const AREA_STATUS: Record<
  AreaSummaryDto['stato'],
  { label: string; tone: 'amber' | 'blue' | 'green' }
> = {
  da_verificare: { label: 'da verificare', tone: 'amber' },
  approfondimento: { label: 'da approfondire', tone: 'blue' },
  completata: { label: 'completata', tone: 'green' },
};

/** Stato di una procedura per la persona: proposta dal settore, confermata, bozza nel sistema. */
function candidateLabel(c: ProcessCandidateDto): {
  label: string;
  tone: 'gray' | 'amber' | 'green' | 'red' | 'blue';
} {
  if (c.status === 'scartato')
    return c.details.scartato === 'una_tantum'
      ? { label: 'tolta: fatta una volta sola', tone: 'gray' }
      : { label: 'non la fate', tone: 'gray' };
  if (c.details.origine !== 'raccontato')
    return { label: 'proposta di settore, da verificare', tone: 'amber' };
  if (c.status === 'adottato') return { label: 'bozza nel sistema, da approvare', tone: 'green' };
  if (drafting(c)) return { label: 'bozza in scrittura…', tone: 'blue' };
  return { label: c.error ? 'bozza da completare' : 'confermata', tone: c.error ? 'red' : 'blue' };
}

/**
 * Procedure dell'azienda divise per area: le prepara il sistema dal settore e dalle fonti verificate; ogni area
 * si verifica e si approfondisce nella sua chat.
 */
export function ProceduresPage({ area }: { area?: string }) {
  if (area) return <AreaChat key={area} n={Number(area)} />;
  return <AreaList />;
}

function AreaList() {
  const { tenant, isManager } = useTenant();
  const state = useTenantQuery<OnboardingState>(['onboarding'], '/onboarding', {
    refetchInterval: (d) => (d?.mapping || d?.areas.some((a) => a.thinking) ? 3000 : false),
  });
  const progress = useSetupProgress(isManager);
  const s = state.data;
  return (
    <div className="mx-auto max-w-4xl">
      <PageTitle
        title="Procedure"
        subtitle="Le procedure standard per un’azienda come la tua, divise per area. Apri un’area per verificarle e raccontare come le fate davvero: quelle confermate diventano bozze da approvare."
      />
      <ErrorText>{state.error ? errorMessage(state.error) : ''}</ErrorText>
      <SetupBanner here="procedure" />
      {s && !s.areas.length && (
        <Empty>
          {s.mapping ? (
            <ActivityLoader activity={s.activity.map} fallback="Preparo le procedure standard…" />
          ) : (
            <>
              Le procedure si preparano da sole dopo la prima lettura della tua azienda.{' '}
              <button
                className="text-accent hover:underline"
                onClick={() => navigate(`/t/${tenant.id}/configurazione`)}
              >
                Vai alla configurazione
              </button>
            </>
          )}
        </Empty>
      )}
      {s && s.areas.length > 0 && (
        <>
          {s.map.sector && <p className="mb-4 text-sm text-ink/60">Settore: {s.map.sector}</p>}
          {s.mapping && <p className="mb-3 text-xs text-ink/50">Sto aggiornando le procedure…</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {s.areas.map((a) => {
              const st = AREA_STATUS[a.stato];
              const phases = progress.data?.aree.find((x) => x.n === a.n && x.area === a.area);
              return (
                <button
                  key={a.n}
                  className="text-left"
                  onClick={() => navigate(`/t/${tenant.id}/procedure/${a.n}`)}
                >
                  <Card className="h-full p-4 transition hover:border-accent/50">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="font-medium">{a.area}</span>
                      <span className="flex-1" />
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                    {phases && (
                      <div className="mb-2">
                        <AreaPhases a={phases} />
                      </div>
                    )}
                    <p className="text-sm text-ink/60">
                      {a.procedure} procedure · {a.confermate} confermate
                      {a.da_verificare ? ` · ${a.da_verificare} da verificare` : ''}
                    </p>
                    {a.strumenti?.modo && (
                      <p className="mt-1 text-xs text-ink/50">Strumenti: {a.strumenti.modo}</p>
                    )}
                    <p className="mt-2 text-xs text-accent">
                      {a.thinking
                        ? 'sto rispondendo…'
                        : a.messaggi
                          ? 'continua la chat'
                          : 'apri la chat'}
                    </p>
                  </Card>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** Chat di approfondimento di un'area, con le sue procedure a fianco. */
function AreaChat({ n }: { n: number }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const key = ['tenant', tenant.id, 'onboarding-area', n];
  const state = useTenantQuery<AreaChatState>(['onboarding-area', n], `/onboarding/areas/${n}`, {
    refetchInterval: (d) => (d?.thinking ? 1500 : d?.candidates.some(drafting) ? 4000 : false),
  });
  const setState = (s: AreaChatState) => queryClient.setQueryData(key, s);
  const [text, setText] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  const send = useMutation({
    mutationFn: (message: string) =>
      api<AreaChatState>(t(`/onboarding/areas/${n}/messages`), { body: { message } }),
    onSuccess: (s) => {
      setText('');
      setState(s);
    },
  });
  const retry = useMutation({
    mutationFn: () => api<AreaChatState>(t(`/onboarding/areas/${n}/retry`), { method: 'POST' }),
    onSuccess: setState,
  });
  // Una procedura tolta per errore torna nel sistema (come ricorrente).
  const restore = useMutation({
    mutationFn: (id: string) =>
      api<OnboardingState>(t(`/onboarding/candidates/${id}/restore`), { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const s = state.data;
  useEffect(() => {
    void bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [s?.messages.length, s?.thinking]);

  const busy = !!s?.thinking || send.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const message = text.trim();
    if (message && !busy) send.mutate(message);
  };
  const lastAssistant = s?.messages.filter((m) => m.role === 'assistant').at(-1)?.id;
  const lastCard = s?.messages.filter((m) => m.changes.verifica).at(-1)?.id;

  return (
    <div className="mx-auto max-w-6xl">
      <button
        className="mb-2 text-sm text-ink/50 hover:text-ink"
        onClick={() => navigate(`/t/${tenant.id}/procedure`)}
      >
        ← Procedure
      </button>
      <PageTitle
        title={s?.area.area ?? 'Area'}
        subtitle={
          s?.area.strumenti?.modo
            ? `Strumenti: ${s.area.strumenti.modo}`
            : 'Verifica le procedure proposte e racconta come le fate davvero.'
        }
      />
      <ErrorText>{state.error ? errorMessage(state.error) : ''}</ErrorText>
      {isManager && <SetupBanner here={`procedure/${n}`} />}
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <section className="flex h-[72vh] min-w-0 flex-col rounded-xl border border-black/10 bg-white shadow-sm">
          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            {s?.messages.map((m) => (
              <MessageBubble
                key={m.id}
                m={m}
                card={m.id === lastCard && !!s.card?.items.some((i) => i.stato === 'da_verificare')}
                actions={m.id === lastAssistant}
              />
            ))}
            {busy && (
              <ActivityLoader activity={s?.activity ?? null} fallback="Leggo la tua risposta…" />
            )}
            {s?.last_error && !s.thinking && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
                Non sono riuscito a rispondere: {s.last_error}{' '}
                <button className="font-medium underline" onClick={() => retry.mutate()}>
                  Riprova
                </button>
              </div>
            )}
            <div ref={bottom} />
          </div>
          <form className="border-t border-black/10 p-3" onSubmit={submit}>
            <ErrorText>{send.error ? errorMessage(send.error) : ''}</ErrorText>
            <div className="mt-1 flex items-end gap-2">
              <textarea
                className="max-h-40 min-h-[2.75rem] flex-1 resize-y rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent"
                placeholder={
                  busy
                    ? 'Sto lavorando: aspetta la mia risposta…'
                    : 'Rispondi con i numeri o racconta come lavorate… (Invio per inviare, Maiusc+Invio per andare a capo)'
                }
                disabled={busy}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit(e);
                  }
                }}
              />
              <Button type="submit" disabled={!text.trim() || busy}>
                Invia
              </Button>
            </div>
          </form>
        </section>
        <aside className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">
            Procedure dell’area
          </p>
          {s?.candidates.map((c, i) => {
            const st = candidateLabel(c);
            return (
              <Card
                key={c.id}
                className={`p-3 text-sm ${c.status === 'scartato' ? 'opacity-50' : ''}`}
              >
                <div className="flex gap-2">
                  <span className="w-5 shrink-0 text-right font-semibold tabular-nums">
                    {i + 1}.
                  </span>
                  <span className="min-w-0 flex-1">
                    {c.process_id ? (
                      <button
                        className="text-left font-medium text-accent hover:underline"
                        onClick={() => navigate(`/t/${tenant.id}/processi/${c.process_id}`)}
                      >
                        {c.name}
                      </button>
                    ) : (
                      <span className="font-medium">{c.name}</span>
                    )}
                    {c.description && (
                      <span className="block text-xs text-ink/50">{c.description}</span>
                    )}
                    <ScopeControl tipo="procedura" id={c.id} ambito={c.ambito} />
                    {!!c.details.riguarda?.length && (
                      <span className="block text-xs text-ink/50">Per: {c.details.riguarda.join(', ')}</span>
                    )}
                    <span className="mt-1 block">
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </span>
                    {c.status === 'scartato' && c.details.scartato_motivo && (
                      <span className="mt-1 block text-xs text-ink/50">
                        {c.details.scartato_motivo}
                      </span>
                    )}
                    {c.status === 'scartato' && (
                      <button
                        className="mt-1 text-xs text-accent hover:underline disabled:opacity-50"
                        disabled={restore.isPending}
                        onClick={() => restore.mutate(c.id)}
                      >
                        Ripristina: la facciamo e si ripete
                      </button>
                    )}
                  </span>
                </div>
              </Card>
            );
          })}
        </aside>
      </div>
    </div>
  );
}
